import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, RefreshCw, Plus, Folder, ChevronRight, ChevronLeft,
  Pill, Calculator, AlertTriangle, TestTube, Lightbulb, 
  XCircle, Minus, Check, Zap, ArrowLeft, Loader2, FolderOpen,
  Download, Sparkles, Trash2, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_TYPES = [
  { id: 'concept', label: 'Concepto', icon: Lightbulb, color: 'amber' },
  { id: 'syndrome', label: 'Síndrome', icon: AlertTriangle, color: 'red' },
  { id: 'formula', label: 'Fórmula', icon: Calculator, color: 'blue' },
  { id: 'dose', label: 'Dosis', icon: Pill, color: 'purple' },
  { id: 'value', label: 'Valor', icon: TestTube, color: 'emerald' }
];

const FlashcardsPage = ({ user }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('menu'); // menu, topics, study, create
  const [stats, setStats] = useState(null);
  const [deckStats, setDeckStats] = useState(null);
  const [dueCards, setDueCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [topics, setTopics] = useState([]);
  const [topicsSummary, setTopicsSummary] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [addingToMazo, setAddingToMazo] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newCard, setNewCard] = useState({
    topic_id: '',
    front: '',
    back: '',
    card_type: 'concept',
    tags: []
  });
  const [cardsToAdd, setCardsToAdd] = useState({}); // {topic_id: count}

  useEffect(() => {
    loadStats();
    loadDueCards();
    loadTopics();
    loadTopicsSummary();
    loadDeckStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/flashcards/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadDueCards = async () => {
    try {
      const token = localStorage.getItem('session_token');
      // Sin límite estricto - el alumno puede estudiar todas las flashcards pendientes
      const res = await axios.get(`${API}/flashcards/due?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDueCards(res.data.cards || []);
    } catch (error) {
      console.error('Error loading due cards:', error);
    }
  };

  const loadTopics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/subjects-with-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allTopics = [];
      (res.data || []).forEach(subject => {
        (subject.topics || []).forEach(topic => {
          allTopics.push({
            ...topic,
            subject_name: subject.name,
            subject_id: subject.subject_id
          });
        });
      });
      setTopics(allTopics);
    } catch (error) {
      console.error('Error loading topics:', error);
    }
  };

  const loadTopicsSummary = async () => {
    setLoadingTopics(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/flashcards/topics-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopicsSummary(res.data.topics || []);
    } catch (error) {
      console.error('Error loading topics summary:', error);
    } finally {
      setLoadingTopics(false);
    }
  };

  const loadDeckStats = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/flashcards/deck-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeckStats(res.data);
    } catch (error) {
      console.error('Error loading deck stats:', error);
    }
  };

  const resetDeck = async (topicId = null) => {
    setResetting(true);
    try {
      const token = localStorage.getItem('session_token');
      const url = topicId 
        ? `${API}/flashcards/reset-deck?topic_id=${topicId}`
        : `${API}/flashcards/reset-deck`;
      
      const res = await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      setShowResetModal(false);
      loadStats();
      loadDueCards();
      loadTopicsSummary();
      loadDeckStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al resetear el mazo');
    } finally {
      setResetting(false);
    }
  };

  const handleReview = async (rating) => {
    if (!dueCards[currentIndex]) return;
    
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/flashcards/review`, {
        card_id: dueCards[currentIndex].card_id,
        rating
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`+${res.data.xp_earned} XP`, { duration: 1500 });

      if (currentIndex < dueCards.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setShowAnswer(false);
      } else {
        toast.success('¡Has completado todas las tarjetas!');
        setMode('menu');
        loadStats();
        loadDueCards();
      }
    } catch (error) {
      toast.error('Error al procesar repaso');
    }
  };

  const handleCreateCard = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/flashcards/cards`, newCard, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Flashcard creada');
      setNewCard({ topic_id: '', front: '', back: '', card_type: 'concept', tags: [] });
      setMode('menu');
      loadStats();
      loadDueCards();
    } catch (error) {
      toast.error('Error al crear flashcard');
    }
  };

  const addSystemFlashcardsToMazo = async (topicId, count) => {
    const numToAdd = count || cardsToAdd[topicId] || 10;
    setAddingToMazo(topicId);
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(
        `${API}/flashcards/add-system-to-deck?topic_id=${topicId}&count=${numToAdd}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      loadTopicsSummary();
      loadDueCards();
      loadStats();
      loadDeckStats();
      // Reset the selector for this topic
      setCardsToAdd(prev => ({ ...prev, [topicId]: undefined }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al añadir flashcards');
    } finally {
      setAddingToMazo(null);
    }
  };

  const getCardsToAddCount = (topicId, available) => {
    return cardsToAdd[topicId] || Math.min(10, available);
  };

  const startStudy = () => {
    if (dueCards.length > 0) {
      setCurrentIndex(0);
      setShowAnswer(false);
      setMode('study');
    }
  };

  const getTypeIcon = (type) => {
    return CARD_TYPES.find(t => t.id === type)?.icon || Lightbulb;
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: { text: 'Olvidé', icon: XCircle },
      2: { text: 'Difícil', icon: Minus },
      3: { text: 'Bien', icon: Check },
      4: { text: 'Fácil', icon: Zap }
    };
    return labels[rating];
  };

  // Group topics by subject
  const topicsBySubject = topicsSummary.reduce((acc, topic) => {
    if (!acc[topic.subject_name]) {
      acc[topic.subject_name] = [];
    }
    acc[topic.subject_name].push(topic);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Button
              variant="ghost"
              onClick={() => mode === 'menu' ? navigate('/dashboard') : setMode('menu')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {mode === 'menu' ? 'Dashboard' : 'Volver'}
            </Button>
            <OposaiLogo size={40} />
            <div className="w-20" />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Main Menu */}
        {mode === 'menu' && (
          <div className="space-y-6">
            {/* Title */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-2xl mb-4">
                <BookOpen className="w-8 h-8" />
                <span className="text-2xl font-bold">Flashcards TuPlazaIA</span>
              </div>
              <p className="text-slate-600">Sistema de repetición espaciada para memorizar conceptos clave</p>
            </div>

            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-white rounded-2xl p-4 text-center border border-slate-100 shadow-sm">
                  <p className="text-3xl font-bold text-indigo-600">{stats.total_cards}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="bg-white rounded-2xl p-4 text-center border border-slate-100 shadow-sm">
                  <p className="text-3xl font-bold text-amber-600">{stats.due_cards}</p>
                  <p className="text-xs text-slate-500">Pendientes</p>
                </div>
                <div className="bg-white rounded-2xl p-4 text-center border border-slate-100 shadow-sm">
                  <p className="text-3xl font-bold text-emerald-600">{stats.reviews_today}</p>
                  <p className="text-xs text-slate-500">Hoy</p>
                </div>
                <div className="bg-white rounded-2xl p-4 text-center border border-slate-100 shadow-sm">
                  <p className="text-3xl font-bold text-purple-600">{stats.average_stability_days}d</p>
                  <p className="text-xs text-slate-500">Retención</p>
                </div>
              </div>
            )}

            {/* Action Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={startStudy}
                className={`rounded-2xl p-6 cursor-pointer ${
                  dueCards.length > 0
                    ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
                    : 'bg-slate-100 text-slate-500'
                }`}
              >
                <RefreshCw className="w-10 h-10 mb-4" />
                <h2 className="text-xl font-bold mb-2">Estudiar Ahora</h2>
                <p className="opacity-80 text-sm">
                  {dueCards.length > 0
                    ? `${dueCards.length} tarjetas pendientes`
                    : 'No hay tarjetas pendientes'}
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('session_token');
                    const res = await axios.get(`${API}/flashcards/difficult?limit=30`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data.cards && res.data.cards.length > 0) {
                      setDueCards(res.data.cards);
                      setCurrentIndex(0);
                      setMode('study');
                      toast.success(`${res.data.cards.length} tarjetas difíciles cargadas`);
                    } else {
                      toast.info('¡No tienes flashcards difíciles! 🎉');
                    }
                  } catch (error) {
                    toast.error('Error al cargar tarjetas difíciles');
                  }
                }}
                className="bg-white rounded-2xl border-2 border-rose-200 p-6 cursor-pointer hover:border-rose-400 transition-colors"
              >
                <XCircle className="w-10 h-10 mb-4 text-rose-500" />
                <h2 className="text-xl font-bold mb-2 text-slate-900">Repasar Difíciles</h2>
                <p className="text-slate-600 text-sm">
                  Practica las que más te cuestan
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMode('topics')}
                className="bg-white rounded-2xl border-2 border-slate-200 p-6 cursor-pointer hover:border-indigo-400 transition-colors"
              >
                <FolderOpen className="w-10 h-10 mb-4 text-indigo-500" />
                <h2 className="text-xl font-bold mb-2 text-slate-900">Por Tema</h2>
                <p className="text-slate-600 text-sm">
                  Flashcards oficiales del temario
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMode('create')}
                className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-6 cursor-pointer hover:border-emerald-400 transition-colors"
              >
                <Plus className="w-10 h-10 mb-4 text-emerald-500" />
                <h2 className="text-xl font-bold mb-2 text-slate-900">Crear Propia</h2>
                <p className="text-slate-600 text-sm">
                  Crea tus flashcards personalizadas
                </p>
              </motion.div>
            </div>

            {/* Card Types */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Tipos de Flashcards
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {CARD_TYPES.map(type => {
                  const Icon = type.icon;
                  return (
                    <div key={type.id} className={`bg-${type.color}-50 rounded-xl p-3 text-center`}>
                      <Icon className={`w-6 h-6 mx-auto mb-2 text-${type.color}-600`} />
                      <p className="text-sm font-medium">{type.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Progress by State */}
            {stats?.by_state && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <h3 className="font-bold mb-4">Progreso por Estado</h3>
                <div className="grid grid-cols-4 gap-3">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-400">{stats.by_state.new || 0}</p>
                    <p className="text-xs text-slate-500">Nuevas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-500">{stats.by_state.learning || 0}</p>
                    <p className="text-xs text-slate-500">Aprendiendo</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-500">{stats.by_state.review || 0}</p>
                    <p className="text-xs text-slate-500">En Repaso</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{stats.by_state.relearning || 0}</p>
                    <p className="text-xs text-slate-500">Reaprendiendo</p>
                  </div>
                </div>
              </div>
            )}

            {/* Deck Management */}
            {stats?.total_cards > 0 && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-indigo-500" />
                    Gestionar Mi Mazo
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowResetModal(true)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Resetear Mazo
                  </Button>
                </div>
                
                {deckStats?.by_topic && deckStats.by_topic.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {deckStats.by_topic.map(topic => (
                      <div 
                        key={topic.topic_id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {topic.topic_number ? `Tema ${topic.topic_number}: ` : ''}{topic.topic_name}
                          </p>
                          <p className="text-xs text-slate-500">{topic.count} flashcards</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`¿Eliminar las ${topic.count} flashcards del tema "${topic.topic_name}" de tu mazo?`)) {
                              resetDeck(topic.topic_id);
                            }
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Reset Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Resetear Mazo</h3>
                  <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
                </div>
              </div>
              
              <p className="text-slate-600 mb-6">
                ¿Estás seguro de que quieres eliminar <strong>todas las {stats?.total_cards} flashcards</strong> de tu mazo? 
                Perderás todo el progreso de estudio y tendrás que volver a añadir los temas que quieras estudiar.
              </p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowResetModal(false)}
                  disabled={resetting}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => resetDeck()}
                  disabled={resetting}
                >
                  {resetting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Eliminar Todo
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Topics Browser Mode */}
        {mode === 'topics' && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
                <FolderOpen className="w-6 h-6 text-indigo-600" />
                Flashcards por Tema
              </h2>
              <p className="text-slate-600">Repasa las flashcards del temario oficial</p>
            </div>

            {loadingTopics ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(topicsBySubject).map(([subjectName, subjectTopics]) => (
                  <div key={subjectName} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 border-b border-slate-100">
                      <h3 className="font-bold flex items-center gap-2">
                        <Folder className="w-5 h-5 text-indigo-600" />
                        {subjectName}
                      </h3>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {subjectTopics.map(topic => (
                        <div key={topic.topic_id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{topic.topic_name}</p>
                            <div className="flex items-center gap-4 mt-1">
                              <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                                {topic.system_flashcards} del sistema
                              </span>
                              <span className="text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                {topic.user_flashcards} personales
                              </span>
                              {topic.due_count > 0 && (
                                <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                                  {topic.due_count} pendientes
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {topic.system_flashcards > 0 && (
                              <div className="flex items-center gap-2">
                                {/* Selector numérico */}
                                <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                                  <button
                                    onClick={() => setCardsToAdd(prev => ({
                                      ...prev,
                                      [topic.topic_id]: Math.max(1, (prev[topic.topic_id] || 10) - 5)
                                    }))}
                                    className="px-2 py-1 hover:bg-slate-100 text-slate-600"
                                    disabled={addingToMazo === topic.topic_id}
                                  >
                                    <Minus className="w-3 h-3" />
                                  </button>
                                  <input
                                    type="number"
                                    min="1"
                                    max={topic.system_flashcards}
                                    value={cardsToAdd[topic.topic_id] || Math.min(10, topic.system_flashcards)}
                                    onChange={(e) => setCardsToAdd(prev => ({
                                      ...prev,
                                      [topic.topic_id]: Math.min(Math.max(1, parseInt(e.target.value) || 1), topic.system_flashcards)
                                    }))}
                                    className="w-14 text-center text-sm font-medium border-x border-slate-200 py-1 focus:outline-none"
                                    disabled={addingToMazo === topic.topic_id}
                                  />
                                  <button
                                    onClick={() => setCardsToAdd(prev => ({
                                      ...prev,
                                      [topic.topic_id]: Math.min(topic.system_flashcards, (prev[topic.topic_id] || 10) + 5)
                                    }))}
                                    className="px-2 py-1 hover:bg-slate-100 text-slate-600"
                                    disabled={addingToMazo === topic.topic_id}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </div>
                                <span className="text-xs text-slate-400">/ {topic.system_flashcards}</span>
                                <Button
                                  size="sm"
                                  onClick={() => addSystemFlashcardsToMazo(topic.topic_id)}
                                  disabled={addingToMazo === topic.topic_id}
                                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                                >
                                  {addingToMazo === topic.topic_id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <>
                                      <Download className="w-3 h-3 mr-1" />
                                      Añadir
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {Object.keys(topicsBySubject).length === 0 && (
                  <div className="text-center py-12 bg-white rounded-2xl">
                    <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No hay flashcards del sistema disponibles todavía</p>
                    <p className="text-sm text-slate-400 mt-2">Se generarán cuando el administrador suba contenido</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Study Mode */}
        {mode === 'study' && dueCards.length > 0 && (
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-4">
              <span className="text-sm text-slate-500">
                {currentIndex + 1} / {dueCards.length}
              </span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
              >
                {/* Card Front */}
                <div className="p-8 min-h-[250px] flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    {(() => {
                      const Icon = getTypeIcon(dueCards[currentIndex]?.card_type);
                      return <Icon className="w-5 h-5 text-amber-600" />;
                    })()}
                    <span className="text-sm text-slate-500 uppercase">
                      {CARD_TYPES.find(t => t.id === dueCards[currentIndex]?.card_type)?.label || 'Concepto'}
                    </span>
                  </div>
                  <p className="text-xl font-medium text-slate-900 text-center">
                    {dueCards[currentIndex]?.front}
                  </p>
                </div>

                <div className="border-t border-slate-100" />

                {showAnswer ? (
                  <div className="p-8 bg-slate-50">
                    <p className="text-lg text-slate-700 text-center mb-6 whitespace-pre-line">
                      {dueCards[currentIndex]?.back}
                    </p>
                    
                    <p className="text-sm text-slate-500 text-center mb-4">¿Cómo de fácil te resultó recordarlo?</p>
                    
                    <div className="grid grid-cols-4 gap-2">
                      {[1, 2, 3, 4].map(rating => {
                        const info = getRatingLabel(rating);
                        const Icon = info.icon;
                        return (
                          <button
                            key={rating}
                            onClick={() => handleReview(rating)}
                            className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                              rating === 1 ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100' :
                              rating === 2 ? 'border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100' :
                              rating === 3 ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' :
                              'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                            }`}
                          >
                            <Icon className="w-5 h-5 mx-auto mb-1" />
                            <p className="text-xs font-medium">{info.text}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="p-8 bg-slate-50">
                    <Button
                      onClick={() => setShowAnswer(true)}
                      className="w-full py-6 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold"
                    >
                      Mostrar Respuesta
                    </Button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* Create Mode */}
        {mode === 'create' && (
          <div className="max-w-xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6">
              <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Plus className="w-5 h-5 text-emerald-600" />
                Nueva Flashcard Personalizada
              </h2>
              
              <form onSubmit={handleCreateCard} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tema *</label>
                  <select
                    value={newCard.topic_id}
                    onChange={(e) => setNewCard({...newCard, topic_id: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                    required
                  >
                    <option value="">Selecciona un tema</option>
                    {topics.map(topic => (
                      <option key={topic.topic_id} value={topic.topic_id}>
                        {topic.subject_name} - {topic.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tipo de tarjeta</label>
                  <div className="grid grid-cols-5 gap-2">
                    {CARD_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setNewCard({...newCard, card_type: type.id})}
                          className={`p-2 rounded-lg border-2 transition-all ${
                            newCard.card_type === type.id
                              ? `border-${type.color}-500 bg-${type.color}-50`
                              : 'border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <Icon className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-xs">{type.label}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Pregunta / Concepto (frente) *
                  </label>
                  <textarea
                    value={newCard.front}
                    onChange={(e) => setNewCard({...newCard, front: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none resize-none"
                    placeholder="Ej: ¿Cuál es el plazo máximo para resolver un procedimiento administrativo según la Ley 39/2015?"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Respuesta (reverso) *
                  </label>
                  <textarea
                    value={newCard.back}
                    onChange={(e) => setNewCard({...newCard, back: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none resize-none"
                    placeholder="Ej: 3 meses (Art. 21.3 Ley 39/2015)&#10;Si no hay plazo específico en la norma reguladora."
                    required
                  />
                </div>

                <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-800">
                  <p className="font-medium mb-2">💡 Consejos para crear buenas flashcards:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>Una sola pregunta/concepto por tarjeta</li>
                    <li>Incluye el artículo o fuente de la información</li>
                    <li>Añade tags para organizar (ej: [Ley 39/2015, Procedimiento])</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMode('menu')}
                    className="flex-1 rounded-xl py-6"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 rounded-xl py-6 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Crear Flashcard
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardsPage;
